import { trace, SpanStatusCode } from '@opentelemetry/api'
import type { Span } from '@opentelemetry/api'
import { getBrowserLocation } from '@repo/env-graphql-url'
import type { BrowserLocation } from '@repo/env-graphql-url'
import { rpcUrlForLocation } from '@repo/env-graphql-url/rpc-connect-url'
import { usesFirstPartySessionBridge } from '@repo/first-party-session'
import { fetcher } from 'codegen/fetcher'

const UserTokenDocument = `query UserToken { me { token } }`

let token: string | undefined = undefined
let tokenExpirationTime: number | undefined = undefined
let tokenRequest: Promise<string | undefined> | undefined = undefined

async function fetchToken() {
  return await fetcher<{ me: { token: string } }, unknown>(UserTokenDocument)()
}

function decodeToken(token: string): { exp?: number; atp?: string } {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return {}
    }
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded
  } catch {
    return {}
  }
}

export async function getToken() {
  if (token && !isTokenExpired()) {
    return token
  }
  if (tokenRequest) {
    return await tokenRequest
  }
  tokenRequest = fetchToken()
    .then(response => {
      if (response?.me?.token) {
        saveRpcToken(response.me.token)
        return token
      }
      return undefined
    })
    .finally(() => {
      tokenRequest = undefined
    })
  return await tokenRequest
}

export function getCurrentToken(): string | undefined {
  return token
}

export function saveRpcToken(nextToken: string) {
  token = nextToken
  const decoded = decodeToken(token)
  if (decoded.exp) {
    tokenExpirationTime = decoded.exp * 1000
  } else {
    tokenExpirationTime = Date.now() + 3_600_000
  }
}

function isTokenExpired(): boolean {
  if (!tokenExpirationTime || !token) {
    return true
  }
  const bufferMs = 30_000
  return Date.now() >= tokenExpirationTime - bufferMs
}

export function invalidateToken() {
  token = undefined
  tokenExpirationTime = undefined
  tokenRequest = undefined
}

/**
 * True when the token identifies an anonymous (temporary) visitor: the gateway
 * mints those with the `atp: "a"` claim. Anonymous visitors never get an
 * `fm_session`, so this is the one identity that still needs its bearer on the
 * first-party session hosts.
 */
export function isAnonymousToken(token: string): boolean {
  return decodeToken(token).atp === 'a'
}

/**
 * The Electron preload installs this. Desktop keeps the login JWT and never
 * gets the website's HttpOnly `fm_session`, so same-origin calls on www.idea.ai
 * still need the bearer.
 */
function isDesktopShell(): boolean {
  return Boolean((globalThis as { studioDesktop?: unknown }).studioDesktop)
}

/**
 * True when the request needs no bearer: the page has the HttpOnly `fm_session`
 * (first-party session hosts such as www.cf.land / www.idea.ai) and the resolved
 * URL is same-origin, so the browser sends that cookie and the gateway's IAM
 * policy authenticates on it. Anything cross-origin keeps the UserToken bearer.
 * The desktop shell never receives that cookie — the exchanged JWT is its only
 * credential — so it keeps the bearer even on www.idea.ai.
 * Callers still attach the bearer for anonymous identities; see `getBearerForRequest`.
 */
export function usesCookieAuth(
  resolvedUrl: string,
  location: BrowserLocation | null = getBrowserLocation(),
): boolean {
  if (isDesktopShell() || !location || !usesFirstPartySessionBridge(location)) {
    return false
  }
  try {
    return new URL(resolvedUrl, location.origin).origin === location.origin
  } catch {
    return false
  }
}

/**
 * The `Authorization` bearer for a resolved request URL, or undefined when the
 * request must go out on the cookie alone. On first-party session hosts a
 * same-origin call carries the bearer only for an anonymous identity (there is
 * no cookie for it); a login identity relies on `fm_session`. Everywhere else
 * the UserToken bearer is attached as before.
 */
export async function getBearerForRequest(resolvedUrl: string): Promise<string | undefined> {
  const bearer = await getToken()
  if (!bearer) {
    return undefined
  }
  if (usesCookieAuth(resolvedUrl) && !isAnonymousToken(bearer)) {
    return undefined
  }
  return bearer
}

const DEFAULT_TIMEOUT = 30_000
export async function fetchRpc<Response, Variables = unknown>({
  url,
  variables,
  headers = {},
  timeout = DEFAULT_TIMEOUT,
  controller: controllerFromCaller,
}: {
  url: string
  variables: Variables
  headers?: HeadersInit
  timeout?: number
  controller?: AbortController
}): Promise<Response> {
  const tracer = trace.getTracer('rpc-codegen')

  const resolvedUrl = rpcUrlForLocation(url, getBrowserLocation())
  const bearer = await getBearerForRequest(resolvedUrl)

  return await tracer.startActiveSpan('fetchRpc', async span => {
    span.setAttribute('http.url', resolvedUrl)

    const controller = controllerFromCaller ?? getAbortController()
    const timeoutId = timeout ? setTimeout(() => controller?.abort(), timeout) : undefined

    try {
      const response = await fetch(resolvedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          ...headers,
        },
        body: JSON.stringify(variables || {}),
        signal: controller?.signal,
      })

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        let details: unknown = undefined
        try {
          const errorBody = await response.json()
          details = errorBody?.details?.[0]?.debug
        } catch {}
        throw new FetchError(
          response.status,
          `HTTP error ${response.status}: ${response.statusText}`,
          details,
        )
      }

      const json = await response.json()

      // this can be improved once we know more of the generic error format from the services
      if ('error' in json && 'message' in json.error && 'code' in json.error) {
        const code = json.error.code || 400
        throw new ServiceError(code, json.error.message)
      }

      span.setStatus({ code: SpanStatusCode.OK })

      return json
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw handleError(span, new ServiceError(408, 'CF_ABORT_ERROR'))
      }
      throw handleError(span, error)
    } finally {
      span.end()
      clearTimeout(timeoutId)
    }
  })
}

export class FetchError extends Error {
  constructor(
    public code: number,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'FetchError'
  }
}

export class ServiceError extends Error {
  constructor(
    public code: number,
    message: string,
  ) {
    super(message)
    this.name = 'ServiceError'
  }
}

function getAbortController() {
  try {
    return new AbortController()
  } catch {
    return undefined
  }
}

function handleError(span: Span, error: unknown) {
  span.setStatus({
    code: SpanStatusCode.ERROR,
    message: error instanceof Error ? error.message : String(error),
  })
  span.end()
  return error
}
